import BigNumber from 'bignumber.js';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { StakingPoolsActions } from '../../../actions/stakingPoolActions';
import { ERC20_ABI } from '../../../blockchain/abi/typescript/Erc_20_Abi';
import { STAKING_ABI } from '../../../blockchain/abi/typescript/Staking_Abi';
import { AVERAGE_BLOCK_TIME } from '../../../constants/blockchain-constants';
import networkConstant from '../../../constants/network.constant';
import moneyHelper from '../../../helpers/money-helper';
import { calculateStakeBalance } from '../../../helpers/stake-helpers';
import { State } from '../../../reducers';
import erc20Service from '../../../services/contracts/erc20.contract';
import {
  getAllPricesLogs,
  getAllTransfersLogs,
  getStakingGraphAddress,
} from '../../../services/staking/staking.subgraph.service';
import { StakingPool } from '../../../services/staking/staking.types';
import web3Service from '../../../services/web3.service';
import { findContract } from '../helpers/StakingContractService';
import { estimatedBlocksIntervalRewards } from '../helpers/StakingPoolCalcs';
import { UnstakeData } from '../types/staking.types';

interface Props {
  pool: StakingPool;
}

//@TODO would be good to move this all to redux with redux-thunk
export function useStakingData({ pool }: Props) {
  const [loading, setLoading] = useState(false);

  const [balance, setBalance] = useState('0');
  const [balanceUsd, setBalanceUsd] = useState('');
  const [dailyReward, setDailyReward] = useState(0);
  const [apr, setApr] = useState<string | undefined>(undefined);
  const [fee, setFee] = useState(0);
  const [tokenBalance, setTokenBalance] = useState('0');

  const [aprLoading, setAprLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [earnedRewardsLoading, setEarnedRewardsLoading] = useState(false);

  const [unstakeData, setUnstakeData] = useState<UnstakeData>({
    amount: 0,
    applicableAt: '0',
    usdAmount: '0',
    unstakingDuration: 0,
  });

  const dispatch = useDispatch();
  const { walletAddress } = useSelector(({ account }: State) => ({
    walletAddress: account.address,
  }));

  const {
    setStakedBalance,
    setApr: setAprRedux,
    setStakedBalanceUsd,
    setEstimatedDailyReward: setDailyRewardStore,
    setEarnedReward: setEarnedRewardStore,
  } = bindActionCreators(StakingPoolsActions, dispatch);

  const {
    setUnstakeData: setUnstakeDataAction,
    setFee: setFeeAction,
    setTokenBalance: setTokenBalanceStore,
  } = bindActionCreators(StakingPoolsActions, dispatch);

  const {
    stakingTokenSymbol,
    stakingContractAddress: stakingAddress,
    networkId,
    stakingTokenAddress,
    stakingTokenDecimals,
  } = pool;

  const { USD: currencies } = useSelector((state: any) => state.basic.currency);
  const currencyUsdValue = currencies[stakingTokenSymbol];

  const StakingContract = findContract({
    networkId,
    abi: STAKING_ABI,
    contractAddress: stakingAddress,
  });

  const TokenContract = findContract({
    networkId,
    abi: ERC20_ABI,
    contractAddress: stakingTokenAddress,
  });
  const web3 = web3Service.getInstanceByNetworkId(networkId);

  async function getEstimatedApr() {
    if (pool.finished) {
      setApr('0');
      setAprRedux({
        apr: '0',
        id: pool.stakingContractAddress,
        networkId,
      });
      setAprLoading(false);
      return;
    }
    if (
      networkId === networkConstant.networkId.ethMainnet ||
      networkId === networkConstant.networkId.ethKovan ||
      networkId === networkConstant.networkId.ethRinkeby
    ) {
      await getEstimatedAprEth();
    } else if (
      networkId === networkConstant.networkId.bscMainnet ||
      networkId === networkConstant.networkId.bscTestnet ||
      networkId === networkConstant.networkId.polyMainnet
    ) {
      await getEstimatedAprBscPoly();
    }
    setAprLoading(false);
  }

  async function getFees() {
    try {
      const fee = await StakingContract.methods.claimingFeePercent().call();
      //@TODO check fee is float or int
      setFee(parseFloat(fee));
      setFeeAction({ fee: parseFloat(fee), id: pool.stakingContractAddress, networkId });
    } catch (error) {
      throw error;
    }
  }

  async function getEstimatedAprEth() {
    setAprLoading(true);
    const [stakingPoolBalance, totalStaked, fromBlock, currentStrategy] = await Promise.all([
      TokenContract.methods.balanceOf(stakingAddress).call(),
      StakingContract.methods.totalStaked().call(),
      web3.eth.getBlockNumber(),
      StakingContract.methods.currentStrategy().call(),
    ]);

    const totalStakedNumber = new BigNumber(totalStaked).plus(stakingPoolBalance).toString(10);
    if (totalStakedNumber === '0') {
      // to fix why???
      setApr('0');
      setAprRedux({
        apr: '0',
        id: pool.stakingContractAddress,
        networkId,
      });
      return;
    }
    if (pool.strategyType === 'SECOND') {
      const block = await web3.eth.getBlock(fromBlock);

      const fromTimestamp: any = block.timestamp;
      const toTimestamp = currentStrategy.endBlockNumber;
      if (toTimestamp <= fromTimestamp) {
        setApr('0');
        setAprRedux({
          apr: '0',
          id: pool.stakingContractAddress,
          networkId,
        });
        return;
      }

      const period = toTimestamp - fromTimestamp;
      const yearBlockPeriod = 3600 * 24 * 365;
      const yearBlocksCount = yearBlockPeriod / period;
      const estimatedBlocks = await estimatedBlocksIntervalRewards(
        fromTimestamp,
        toTimestamp,
        currentStrategy,
      );
      const yearRewards = yearBlocksCount * (estimatedBlocks as any);
      setApr(((yearRewards / totalStaked) * 100).toFixed(2));
      setAprRedux({
        apr: ((yearRewards / totalStaked) * 100).toFixed(2),
        id: pool.stakingContractAddress,
        networkId,
      });
      return;
    }

    const toBlock = currentStrategy.endBlockNumber;

    if (toBlock <= fromBlock) {
      setApr('0');
      setAprRedux({
        apr: '0',
        id: pool.stakingContractAddress,
        networkId,
      });
      return;
    }

    const period = toBlock - fromBlock;
    const yearBlockPeriod = (3600 * 24 * 365) / AVERAGE_BLOCK_TIME;
    const yearBlocksCount = yearBlockPeriod / period;
    const estimatedBlocks = await estimatedBlocksIntervalRewards(
      fromBlock,
      toBlock,
      currentStrategy,
    );

    const yearRewards = yearBlocksCount * (estimatedBlocks as any);
    setApr(((yearRewards / totalStaked) * 100).toFixed(2));
    setAprRedux({
      apr: ((yearRewards / totalStaked) * 100).toFixed(2),
      id: pool.stakingContractAddress,
      networkId,
    });
    setAprLoading(false);
  }

  async function getEstimatedAprBscPoly() {
    setAprLoading(true);
    const [totalStaked, fromBlock, currentStrategy] = await Promise.all([
      StakingContract.methods.totalStaked().call(),
      web3.eth.getBlockNumber(),
      StakingContract.methods.currentStrategy().call(),
    ]);

    const { timestamp: fromTimestamp } = await web3.eth.getBlock(fromBlock);

    const totalStakedNumber = new BigNumber(totalStaked).toString(10);
    if (totalStakedNumber === '0') {
      setApr('0');
      setAprRedux({
        apr: '0',
        id: pool.stakingContractAddress,
        networkId,
      });
      return;
    }

    const toTimestamp = Math.max(currentStrategy.endsAt || currentStrategy.endBlockNumber);
    if (toTimestamp <= fromTimestamp) {
      setApr('0');
      setAprRedux({
        apr: '0',
        id: pool.stakingContractAddress,
        networkId,
      });
    }

    const period = toTimestamp - (fromTimestamp as number);
    const yearBlockPeriod = 3600 * 24 * 365;
    const yearBlocksCount = yearBlockPeriod / period;
    const estimatedBlocks = await estimatedBlocksIntervalRewards(
      fromTimestamp as number,
      toTimestamp,
      currentStrategy,
    );
    const yearRewards = yearBlocksCount * (estimatedBlocks as number);

    setApr(((yearRewards / totalStaked) * 100).toFixed(2));
    setAprRedux({
      apr: ((yearRewards / totalStaked) * 100).toFixed(2),
      id: pool.stakingContractAddress,
      networkId,
    });

    setAprLoading(false);
  }

  async function getBalance() {
    setBalanceLoading(true);
    const stakeBalance = await StakingContract.methods.balanceOf(walletAddress).call();
    const [totalSupply, totalStaked, lockedRewards, unlockedRewards, currentStrategy] =
      await Promise.all([
        StakingContract.methods.totalSupply().call(),
        StakingContract.methods.totalStaked().call(),
        StakingContract.methods.lockedRewards().call(),
        StakingContract.methods.getUnlockedRewards().call(),
        StakingContract.methods.currentStrategy().call(),
      ]);
    const price = new BigNumber(totalStaked)
      .plus(BigNumber.min(unlockedRewards.unlocked, lockedRewards))
      .div(totalSupply);
    const balance = calculateStakeBalance(stakeBalance, price);

    if (totalSupply === '0') {
      setBalance('0');
      setBalanceLoading(false);
      return;
    }

    let dayBlockPeriod;
    if (pool.strategyType === 'BLOCK') {
      dayBlockPeriod = (3600 * 24) / (networkId === 1 ? AVERAGE_BLOCK_TIME : 1);
    } else {
      dayBlockPeriod = 3600 * 24;
    }
    const dailyReward =
      currentStrategy.perBlockReward * dayBlockPeriod * (stakeBalance / totalSupply);
    const dailyRewardStore = new BigNumber(dailyReward).div(10 ** stakingTokenDecimals).toFixed();
    setDailyReward(dailyReward);
    setDailyRewardStore({
      id: pool.stakingContractAddress,
      dailyReward: dailyRewardStore === 'NaN' ? '0' : dailyRewardStore,
      networkId,
    });
    const shiftedBalance = new BigNumber(balance).shiftedBy(stakingTokenDecimals * -1).toFixed();
    setBalance(shiftedBalance);

    //Redux call
    setStakedBalance({ balance: shiftedBalance, id: pool.stakingContractAddress, networkId });

    const usdBalance = new BigNumber(balance)
      .div(new BigNumber(10 ** stakingTokenDecimals))
      .times(currencyUsdValue)
      .toFixed(2);

    setBalanceUsd(usdBalance);

    setStakedBalanceUsd({
      balance: usdBalance === 'NaN' ? '0' : usdBalance,
      id: pool.stakingContractAddress,
      networkId,
    });
    setBalanceLoading(false);
  }

  async function getUnstakes() {
    setLoading(true);
    try {
      const { amount, applicableAt } = await StakingContract.methods
        .getUnstake(walletAddress)
        .call();
      const unstakingDuration = await StakingContract.methods.unstakingTime().call();

      // if (amount !== '0') {
      const decimalAmount = new BigNumber(amount)
        .dividedBy(new BigNumber(10).pow(stakingTokenDecimals))
        .toNumber();

      const usdAmount = moneyHelper.formatUSDLong(decimalAmount * currencyUsdValue);

      setUnstakeData({
        amount: decimalAmount,
        applicableAt,
        usdAmount,
        unstakingDuration: parseFloat(unstakingDuration),
      });

      setUnstakeDataAction({
        unstakeData: {
          amount: decimalAmount,
          usdAmount,
          applicableAt,
          unstakingDuration: parseFloat(unstakingDuration),
        },
        id: pool.stakingContractAddress,
        networkId,
      });
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function getTokenBalance() {
    try {
      const accountBalance = await erc20Service.balanceOf(
        pool.networkId,
        ERC20_ABI,
        pool.stakingTokenAddress,
        walletAddress,
      );

      const scaledAccountBalance = new BigNumber(accountBalance)
        .div(new BigNumber(Math.pow(10, pool.stakingTokenDecimals)))
        .toFixed();
      setTokenBalance(scaledAccountBalance);
      setTokenBalanceStore({
        balance: scaledAccountBalance,
        id: pool.stakingContractAddress,
        networkId,
      });
    } catch (error) {
      throw error;
    }
  }

  async function getEarnedRewards() {
    setEarnedRewardsLoading(true);
    try {
      const LogType = {
        SENDED: 'SENDED',
        RECEIVED: 'RECEIVED',
      };

      const GRAPH_ADDRESS = getStakingGraphAddress(networkId);

      const transfersLogs = await getAllTransfersLogs(stakingTokenSymbol, GRAPH_ADDRESS);
      const pricesLogs = await getAllPricesLogs(stakingTokenSymbol, GRAPH_ADDRESS);
      const receivingLogs = transfersLogs.filter((item: any) => item.to === walletAddress);
      const sendingLogs = transfersLogs.filter((item: any) => item.from === walletAddress);

      const blockPrices = new Map();

      pricesLogs.forEach((log: any) => {
        blockPrices.set(log.blockNumber, new BigNumber(log.mantissa));
      });

      const logs = [...receivingLogs, ...sendingLogs]
        .map(({ from, blockNumber, value, logIndex }: any) => {
          const type = from === walletAddress ? LogType.SENDED : LogType.RECEIVED;
          return {
            type,
            block: blockNumber,
            value: new BigNumber(value),
            index: logIndex,
          };
        })
        .sort((a, b) => {
          if (a.block < b.block) return -1;
          if (a.block > b.block) return 1;
          return a.index - b.index;
        });

      let balance = new BigNumber(0);
      let staked = new BigNumber(0);
      let reward = new BigNumber(0);

      logs.forEach((log) => {
        const { type, value, block } = log;
        const price = blockPrices.get(block);
        if (!price) throw new Error(`Unable to get price of block #${block}`);
        if (type === LogType.RECEIVED) {
          staked = staked.plus(value.times(price).div(1e18));
          balance = balance.plus(value);
        } else {
          reward = reward.plus(
            balance
              .times(price)
              .div(10 ** 18)
              .minus(staked.plus(reward)),
          );
          const sended = value.times(price).div(1e18);
          if (reward.lt(sended)) {
            staked = staked.minus(sended.minus(reward));
            reward = new BigNumber(0);
          } else reward = reward.minus(sended);
          balance = balance.minus(value);
        }
      });
      const currentPriceBn = await getPrice();
      const currentPrice = currentPriceBn.toFixed();

      const notLockedReward = balance
        .times(currentPrice === 'NaN' ? 0 : currentPrice)
        .minus(staked.plus(reward));
      const totalReward = reward.plus(notLockedReward);
      const shiftedRewards = totalReward.dividedBy(10 ** stakingTokenDecimals).toFixed();
      setEarnedRewardStore({
        id: pool.stakingContractAddress,
        rewardsEarned: shiftedRewards === 'NaN' ? '0' : shiftedRewards,
        networkId,
      });
    } catch (error) {
      throw error;
    } finally {
      setEarnedRewardsLoading(false);
    }
  }

  async function getPrice() {
    const [totalSupply, totalStaked, lockedRewards, unlockedRewards] = await Promise.all([
      StakingContract.methods.totalSupply().call(),
      StakingContract.methods.totalStaked().call(),
      StakingContract.methods.lockedRewards().call(),
      StakingContract.methods.getUnlockedRewards().call(),
    ]);
    return new BigNumber(totalStaked)
      .plus(BigNumber.min(unlockedRewards.unlocked, lockedRewards))
      .div(totalSupply);
  }

  return {
    balance,
    balanceUsd,
    dailyReward,
    loading,
    aprLoading,
    balanceLoading,
    apr,
    unstakeData,
    fee,
    tokenBalance,
    earnedRewardsLoading,
    getEarnedRewards,
    getTokenBalance,
    setLoading,
    getUnstakes,
    getBalance,
    getEstimatedApr,
    getFees,
  };
}
